<script setup lang="ts" generic="MarkerData = any">
import * as mc from "@googlemaps/markerclusterer";
import { debounceFilter } from "@vueuse/core";
import { createApp as createVueApp } from "vue";

const attrs = useAttrs();

const emit = defineEmits<{
  "update:bounds": [value: google.maps.LatLngBoundsLiteral];
  openFullscreen: [];
  closeFullscreen: [];
}>();

const props = withDefaults(
  defineProps<{
    options?: google.maps.MapOptions;
    markers?: {
      id?: string;
      bounce?: boolean;
      data?: MarkerData;
      marker: google.maps.marker.AdvancedMarkerElementOptions;
    }[];
    bounds?: google.maps.LatLngBoundsLiteral;
    defaultBounds?: google.maps.LatLngBoundsLiteral;
    clusterer?: {
      enabled: boolean;
      options?: mc.ClusterOptions;
    };
    window?: boolean;
    zoomable?: boolean;

    showZoom?: boolean;

    showFullscreen?: boolean;
    isFullscreen?: boolean;
  }>(),
  {
    options: () => ({}),
    markers: () => [],
    bounds: undefined,
    clusterer: undefined,
    window: false,

    showZoom: false,

    showFullscreen: false,
    isFullscreen: false,
  }
);

const slots = defineSlots<{
  ["marker"]: (props: {
    id?: string;
    bounce?: boolean;
    data?: MarkerData;
    marker: google.maps.marker.AdvancedMarkerElementOptions;
  }) => VNode[];

  ["marker-clusterer"]: (props: { item: mc.Cluster }) => VNode[];

  ["window"]: (props: { data?: MarkerData }) => VNode[];
}>();

const options = computed(() => ({
  ...props.options,
  restriction: {
    latLngBounds: { north: 85, south: -85, west: -180, east: 180 },
    strictBounds: true,
  },
  isFractionalZoomEnabled: true,
  zoomControl: false,
  streetViewControl: false,
  scaleControl: false,
  rotateControl: false,
  cameraControl: false,
  mapTypeControl: false,
  fullscreenControl: false,
}));

const mapElement = templateRef<HTMLDivElement>("mapElement");

// --- Google Maps ---
const googleMaps = shallowRef<typeof google.maps>();
const googleMapsMarker = shallowRef<google.maps.MarkerLibrary>();
const map = shallowRef<google.maps.Map>();

const { load } = useScriptGoogleMaps();

const initMap = async () => {
  const script = await load();
  googleMaps.value = (await script.maps) as any as typeof google.maps;

  map.value = new googleMaps.value.Map(mapElement.value, options.value);
  map.value.addListener("bounds_changed", onBoundsChanged);

  googleMapsMarker.value = (await google.maps.importLibrary(
    "marker"
  )) as any as google.maps.MarkerLibrary;

  setMarkers(props.markers);
  setClusterer(props.clusterer?.enabled);
  setBounds(props.defaultBounds || props.bounds);
};

onMounted(initMap);

const waitForGoogleMaps = async () => {
  while (!googleMaps.value) {
    await new Promise((r) => setTimeout(r, 100));
  }
};

const waitForMap = async () => {
  while (!map.value) {
    await new Promise((r) => setTimeout(r, 100));
  }
};

const waitForMarker = async () => {
  while (!googleMapsMarker.value) {
    await new Promise((r) => setTimeout(r, 100));
  }
};

// --- View bounds ---
const bounds = useVModel(props, "bounds", emit, { passive: true });

const setBounds = (value?: google.maps.LatLngBoundsLiteral) => {
  if (isDefined(map) && isDefined(value)) {
    map.value.fitBounds(value, 50);
  }
};

const { ignoreUpdates: ignoreBoundsUpdates } = watchIgnorable(
  bounds,
  setBounds,
  { eventFilter: debounceFilter(500) }
);

const onBoundsChanged = useDebounceFn(() => {
  if (isDefined(map)) {
    const currentBounds = map.value.getBounds();

    if (isDefined(currentBounds)) {
      const northEast = currentBounds.getNorthEast();
      const southWest = currentBounds.getSouthWest();

      ignoreBoundsUpdates(() => {
        bounds.value = {
          east: northEast.lng(),
          north: northEast.lat(),
          south: southWest.lat(),
          west: southWest.lng(),
        };
      });
    }
  }
}, 500);

// --- Marker Clusterer ---
const clusterer = shallowRef<mc.MarkerClusterer>();

const createClusterMarkerContent = (
  item: mc.Cluster
): Node | null | undefined => {
  const slot = slots["marker-clusterer"]?.({ item });
  const vnode = slot?.[0];

  if (vnode) {
    const el = document.createElement("div");
    const app = createVueApp({ render: () => vnode });
    const mounted = app.mount(el);
    const html = mounted.$el.outerHTML;

    const parser = new DOMParser();
    const content = parser.parseFromString(html, "text/html").body.firstChild;

    return content;
  } else {
    return undefined;
  }
};

const setClusterer = async (enabled?: boolean) => {
  await waitForGoogleMaps();
  await waitForMap();
  await waitForMarker();

  if (enabled) {
    if (isDefined(googleMaps) && isDefined(map)) {
      if (props.clusterer?.enabled) {
        nextTick(() => {
          if (!clusterer.value) {
            clusterer.value = new mc.MarkerClusterer({
              map: toRaw(map.value),
              markers: markers.value.map((item) => item.marker),
              renderer: {
                render: (item) => {
                  const content = createClusterMarkerContent(item);

                  watch(
                    bouncingMarker,
                    (marker) => {
                      const markers =
                        item.markers as google.maps.marker.AdvancedMarkerElement[];

                      const bounce = markers.some((m) =>
                        m.element.querySelector(`[id="${marker?.id}"]`)
                      );

                      if (bounce) {
                        content?.firstChild?.parentElement?.classList.add(
                          "animate-marker-bounce"
                        );
                      } else {
                        content?.firstChild?.parentElement?.classList.remove(
                          "animate-marker-bounce"
                        );
                      }
                    },
                    { immediate: true }
                  );

                  return new googleMapsMarker.value!.AdvancedMarkerElement({
                    position: item.position,
                    title: item.count.toString() || "",
                    content,
                  });
                },
              },
            });
          }
        });
      } else {
        clusterer.value?.clearMarkers();
      }
    }
  } else {
    clusterer.value?.clearMarkers();
    clusterer.value = undefined;
  }
};

watch(() => props.clusterer?.enabled, setClusterer);

// --- Markers ---
const markers = ref<
  {
    id?: string;
    bounce?: boolean;
    data?: MarkerData | any;
    marker: google.maps.marker.AdvancedMarkerElement;
  }[]
>([]);

const bouncingMarker = computed(() =>
  markers.value.find((item) => item.bounce)
);

const createMarkerContent = (marker: {
  id?: string;
  bounce?: boolean;
  data?: MarkerData;
  marker: google.maps.marker.AdvancedMarkerElementOptions;
}): Node | null | undefined => {
  const slot = slots["marker"]?.(marker);
  const vnode = slot?.[0];

  if (vnode) {
    const el = document.createElement("div");

    const app = createVueApp({ render: () => vnode });
    const mounted = app.mount(el);

    mounted.$el.id = marker.id || "";

    if (marker.bounce) {
      mounted.$el.classList.add("animate-marker-bounce");
    }

    const html = mounted.$el.outerHTML;

    const parser = new DOMParser();
    const content = parser.parseFromString(html, "text/html").body.firstChild;

    return content;
  } else {
    return undefined;
  }
};

const setMarkers = async (
  items?: {
    id?: string;
    bounce?: boolean;
    data?: MarkerData;
    marker: google.maps.marker.AdvancedMarkerElementOptions;
  }[]
) => {
  await waitForGoogleMaps();
  await waitForMap();
  await waitForMarker();

  if (items) {
    markers.value = markers.value.filter((item) => {
      if (item.id && items.find((i) => i.id === item.id)) {
        return true;
      } else {
        item.marker.map = null;
        clusterer.value?.removeMarker(item.marker);
        return false;
      }
    });

    items.forEach((item) => {
      const { data, id, bounce } = item;
      if (isDefined(googleMaps) && isDefined(map)) {
        const markerItemIndex = markers.value.findIndex((m) => m.id === id);
        const markerItem = markers.value[markerItemIndex];

        let marker = markerItem?.marker;

        if (marker) {
          marker.content = createMarkerContent(item);

          markers.value[markerItemIndex] = { id, bounce, data, marker };
        } else {
          marker = new googleMapsMarker.value!.AdvancedMarkerElement({
            ...item.marker,
            map: toRaw(map.value),
            content: createMarkerContent(item),
            gmpClickable: true,
          });

          markers.value.push({ id, bounce, data, marker });

          if (props.window) {
            marker.addListener("click", () => openWindow(marker!, item.data));
          }

          clusterer.value?.addMarker(marker);
        }
      }
    });
  }

  nextTick(() => {
    clusterer.value?.render();
  });
};

watchDeep(() => props.markers, setMarkers);

// --- Window ---
const window = shallowRef<google.maps.InfoWindow>();

const createWindowContent = (data?: MarkerData): Node | null | undefined => {
  const slot = slots["window"]?.({ data });
  const vnode = slot?.[0];

  if (vnode) {
    const el = document.createElement("div");
    const app = createVueApp({ render: () => vnode });
    const mounted = app.mount(el);
    const html = mounted.$el.outerHTML;

    const parser = new DOMParser();
    const content = parser.parseFromString(html, "text/html").body.firstChild;

    return content;
  } else {
    return undefined;
  }
};

const openWindow = async (
  marker: google.maps.marker.AdvancedMarkerElement,
  data?: MarkerData
) => {
  await waitForGoogleMaps();
  await waitForMap();

  if (isDefined(googleMaps) && isDefined(map)) {
    if (!window.value) {
      window.value = new googleMaps.value.InfoWindow();
    }

    const content = createWindowContent(data) as HTMLElement;

    window.value.setOptions({
      headerDisabled: true,
      disableAutoPan: false,
      pixelOffset: new google.maps.Size(-253 / 2, 253, "px", "%"),
      zIndex: 10,
    });

    map.value?.addListener("click", closeWindow);

    window.value.setContent(content);
    window.value.open(map.value, marker);
  }
};

const closeWindow = () => {
  window.value?.close();
};

// --- Custom controls ---

const zoomIn = () => {
  map.value?.setZoom(map.value?.getZoom()! + 1);
};

const zoomOut = () => {
  map.value?.setZoom(map.value?.getZoom()! - 1);
};

const openFullscreen = () => {
  emit("openFullscreen");
};

const closeFullscreen = () => {
  emit("closeFullscreen");
};

// --- Expose ---
defineExpose({
  mapElement,
  map,
  googleMaps,
  googleMapsMarker,
  clusterer,
});
</script>

<template>
  <div class="app-map" ref="mapElement" v-bind="attrs"></div>

  <AppMapActions
    v-if="props.isFullscreen || props.showFullscreen"
    vertical="top"
    horizontal="end"
  >
    <AppMapActionsButton
      v-if="props.isFullscreen"
      @click="closeFullscreen"
      rounded
    >
      <Icon name="ns:close-big" class="w-4 h-4" />
    </AppMapActionsButton>

    <AppMapActionsButton v-if="props.showFullscreen" @click="openFullscreen">
      <Icon name="ns:maximize-2" class="w-5 h-5" />
    </AppMapActionsButton>
  </AppMapActions>

  <AppMapActions v-if="props.showZoom" vertical="bottom" horizontal="end">
    <AppMapActionsButton @click="zoomIn" :rounded="props.isFullscreen">
      <Icon name="ns:plus" class="w-5 h-5" />
    </AppMapActionsButton>

    <AppMapActionsButton @click="zoomOut" :rounded="props.isFullscreen">
      <Icon name="ns:minus" class="w-5 h-5" />
    </AppMapActionsButton>
  </AppMapActions>
</template>

<style lang="scss">
.app-map {
  .gm-style-iw-t {
    top: 0 !important;
    left: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
  }

  .gm-style-iw-tc {
    display: none;
  }

  .gm-style-iw,
  .gm-style-iw-c {
    box-shadow: 0 0 0.3875rem 0.125rem #0000001a !important;

    border-top-left-radius: 2rem !important;
    border-top-right-radius: 0 !important;
    border-bottom-left-radius: 2rem !important;
    border-bottom-right-radius: 2rem !important;

    padding: 1.25rem !important;

    width: 15.8125rem;

    transform: translate(-100%, 0) !important;
  }

  .gm-style-iw-d {
    padding: 0 !important;
    overflow-x: hidden !important;
    overflow-y: hidden !important;
  }
}
</style>
